import React, { useContext, useEffect, useMemo, useState } from "react";
import { BASE_URL } from "../../constants";
import axios from "axios";
import EditableGrid, { AVAILABLE_COLUMNS } from "./EditableGrid";
import AppContext from "../../context/app-context";
import { toast } from "react-toastify";
import { formatDate } from "@telerik/kendo-intl";
import { v4 as uuidv4 } from "uuid";

const ScheduleForm = ({ closeModel, customers }) => {
  const authCtx = useContext(AppContext);
  const defaultCustomerForm = {
    id: uuidv4(),
    customerId: null,
    time: null,
    frequency: "Daily",
    startDate: null,
    endDate: null,
    recipients: "",
    reportColumns: [],
    active: true,
    inEdit: true,
  };
  const [customerForm, setCustomerForm] = useState(defaultCustomerForm);
  const [popupCustomers, setCustomers] = useState(customers);
  const [scheduleData, setScheduleData] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [search, setSearch] = useState("");

  const frequencyOptions = ["Daily", "Weekly", "Monthly", "Bi-Monthly"];

  // New rows stay pinned to the top; the rest are alphabetical by client name.
  // Rows being edited are never filtered out, so a search can't hide unsaved work.
  const visibleData = useMemo(() => {
    const term = search.trim().toLowerCase();
    return scheduleData
      .filter((item) => {
        if (!term || item.isNew || item.inEdit) return true;
        return (
          (item.customerName || "").toLowerCase().includes(term) ||
          (item.recipients || "").toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        if (!!a.isNew !== !!b.isNew) return a.isNew ? -1 : 1;
        return (a.customerName || "").localeCompare(
          b.customerName || "",
          undefined,
          { sensitivity: "base" }
        );
      });
  }, [scheduleData, search]);

  const formatTime = (dateString, returnType) => {
    if (!(dateString instanceof Date) || isNaN(dateString)) {
      console.error("Invalid date provided:", dateString);
      return null; // or some default value
    }
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert hours to 12-hour format
    const formattedhours = hours % 12 || 12;
    if (returnType === "display")
      return `${String(formattedhours).padStart(2, "0")}:${minutes} ${ampm}`;
    else if (returnType === "toApi")
      return `${String(hours).padStart(2, "0")}:${minutes}:00`;
    else
      return {
        hour: hours,
        minute: minutes,
      };
  };

  const onAddData = () => {
    const newData = {
      ...defaultCustomerForm,
      id: uuidv4(),
      isNew: true,
    };
    setCustomerForm(newData);
    // Clear the search so an active filter can't hide the row we just added.
    setSearch("");
    setScheduleData((pre) => [
      newData,
      ...pre.map((x) => ({ ...x, inEdit: false })),
    ]);
  };

  function isString(value) {
    return typeof value === "string" || value instanceof String;
  }

  const parseScheduleTime = (value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value) ? null : value;
    }

    if (typeof value === "string") {
      if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
        const [hours, minutes] = value.split(":").map((x) => Number(x));
        const parsed = new Date();
        parsed.setHours(hours || 0, minutes || 0, 0, 0);
        return parsed;
      }

      const parsed = new Date(value);
      return isNaN(parsed) ? null : parsed;
    }

    return null;
  };

  const onSave = async () => {
    setButtonLoading(true);
    const schedules = scheduleData.map((item) => ({
      id: isString(item.id) ? 0 : item.id,
      isActive: item.active,
      customerId: item.customerId,
      // ensure time is sent as HH:mm:ss string for API
      time: item.time ? formatTime(item.time, "toApi") : null,
      frequency: item.frequency,
      startDate: item.startDate,
      endDate: item.endDate,
      recipients: item.recipients,
      reportColumns:
        item.reportColumns && item.reportColumns.length > 0
          ? JSON.stringify(item.reportColumns.map((c) => c.key))
          : null,
      createdUserId: item?.createdUserId || authCtx.profile.id,
      creationDate: item?.creationDate || new Date(),
    }));
    const res = await axios.post(`${BASE_URL}/CustomerScheduler`, schedules);
    if (res.status === 200) {
      toast.success("schedules updated successfully!");
    } else {
      toast.error("An error occured while updating schedules!");
    }
    setButtonLoading(false);
  };

  const loadData = async () => {
    setGridLoading(true);
    const res = await axios.get(`${BASE_URL}/CustomerScheduler`);
    if (res.status === 200) {
      if (res.data.length > 0) {
        setScheduleData(
          res.data.map((x) => ({
            ...x,
            active: x.isActive,
            frequency: x.frequency || "Daily",
            time: parseScheduleTime(x.time),
            startDate: x.startDate ? new Date(x.startDate) : null,
            endDate: x.endDate ? new Date(x.endDate) : null,
            recipients: x.recipients || "",
            reportColumns: (() => {
              if (!x.reportColumns) return [];
              try {
                const keys = JSON.parse(x.reportColumns);
                return AVAILABLE_COLUMNS.filter((c) => keys.includes(c.key));
              } catch {
                return [];
              }
            })(),
            customerName:
              customers.find((y) => y.id === x.customerId)?.name || "",
            displayTime: parseScheduleTime(x.time)
              ? formatDate(parseScheduleTime(x.time), "t")
              : "",
            lastRefreshed:
              x.lastRefreshed !== null
                ? formatDate(new Date(x.lastRefreshed), "yyyy-MM-dd") +
                  " " +
                  formatDate(new Date(x.lastRefreshed), "t")
                : null,
          }))
        );
        const existingCustomerIds = res.data.map((x) => x.customerId);
        updateCustomersDropdown(existingCustomerIds);
      } else setScheduleData([defaultCustomerForm]);
    } else {
      toast.error("An error occured while getting schedules!");
    }
    setGridLoading(false);
  };

  const updateCustomersDropdown = (customerIds) => {
    // use latest popupCustomers state rather than original prop
    setCustomers((prev) => prev.filter((x) => !customerIds.includes(x.id)));
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="scheduler-popup bg-zinc-100 rounded p-4 w-full h-full overflow-hidden">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-baseline gap-3">
            <h2 className="text-2xl font-bold">Scheduler</h2>
            <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
              {search.trim()
                ? `${visibleData.length} of ${scheduleData.length}`
                : `${scheduleData.length} scheduled`}
            </span>
          </div>
          <button onClick={closeModel} className=" p-2 rounded text-white">
            <img src="closeIcon.svg" className="h-4 text-white" />
          </button>
        </div>

        <div className="flex mb-4 gap-2 justify-end items-center">
        <div className="relative mr-auto w-full max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client name or recipient"
            className="h-9 w-full rounded-md border border-slate-300 bg-white pl-3 pr-8 text-sm outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-lg leading-none text-slate-400 hover:text-slate-600"
            >
              ×
            </button>
          )}
        </div>
        <button
          type="button"
          className="inline-flex h-9 w-28 float-right justify-center py-2 px-4 border-2 border-sky-600 shadow-sm text-sm font-semibold rounded-md bg-sky-600 text-white focus:outline-none disabled:bg-sky-400 disabled:cursor-not-allowed disabled:border-sky-400 disabled:shadow-none"
          onClick={() => onAddData()}
          disabled={buttonLoading}
        >
          + Add
        </button>
        <button
          type="button"
          className="inline-flex h-9 w-28 float-right justify-center py-2 px-4 border-2 border-green-600 shadow-sm text-sm font-semibold rounded-md bg-green-600 text-white focus:outline-none disabled:bg-green-400 disabled:cursor-not-allowed disabled:border-green-400 disabled:shadow-none"
          onClick={() => onSave()}
          disabled={buttonLoading}
        >
          {buttonLoading && (
            <svg
              aria-hidden="true"
              role="status"
              className="mr-2 inline h-5 w-5 animate-spin text-green-400"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              ></path>
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="#FFF"
              ></path>
            </svg>
          )}
          Save
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden rounded-lg bg-white shadow-[0_0_4px_rgba(0,0,0,0.1)]">
        <EditableGrid
          data={visibleData}
          setData={setScheduleData}
          customers={popupCustomers}
          frequencyOptions={frequencyOptions}
          formatTime={formatTime}
          setEditingForm={setCustomerForm}
          editingForm={customerForm}
          loading={gridLoading}
          updateCustomersDropdown={updateCustomersDropdown}
        />
      </div>
      </div>
    </div>
  );
};

export default ScheduleForm;
